import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Package,
  Truck,
  Warehouse,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Building,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  BarChart3,
  RefreshCw,
  Download,
  Upload,
  ShoppingCart,
  Users,
  Globe,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatDate } from '../lib/utils';

// Dummy data for inventory items
const dummyInventory = [
  {
    id: 'INV-001',
    productName: 'Blue Mountain Coffee - Premium Blend',
    sku: 'BMC-PREM-001',
    category: 'Coffee',
    currentStock: 45,
    minStock: 20,
    maxStock: 200,
    unitCost: 12.50,
    unitPrice: 24.99,
    supplier: 'Blue Mountain Farms',
    location: 'Warehouse A - Section 1',
    lastRestocked: new Date('2024-01-20'),
    expiryDate: new Date('2024-12-31'),
    status: 'in_stock',
  },
  {
    id: 'INV-002',
    productName: 'Jerk Seasoning Blend',
    sku: 'JSB-ORIG-002',
    category: 'Spices',
    currentStock: 8,
    minStock: 15,
    maxStock: 100,
    unitCost: 3.25,
    unitPrice: 8.99,
    supplier: 'Jamaica Spice Co.',
    location: 'Warehouse A - Section 2',
    lastRestocked: new Date('2024-01-15'),
    expiryDate: new Date('2025-06-30'),
    status: 'low_stock',
  },
  {
    id: 'INV-003',
    productName: 'Coconut Oil - Extra Virgin',
    sku: 'CO-EV-003',
    category: 'Oils',
    currentStock: 0,
    minStock: 10,
    maxStock: 75,
    unitCost: 8.75,
    unitPrice: 16.99,
    supplier: 'Tropical Oils Ltd.',
    location: 'Warehouse B - Section 1',
    lastRestocked: new Date('2024-01-10'),
    expiryDate: new Date('2025-01-10'),
    status: 'out_of_stock',
  },
  {
    id: 'INV-004',
    productName: 'Plantain Chips - Sea Salt',
    sku: 'PC-SS-004',
    category: 'Snacks',
    currentStock: 156,
    minStock: 25,
    maxStock: 300,
    unitCost: 2.10,
    unitPrice: 5.99,
    supplier: 'Caribbean Snacks Inc.',
    location: 'Warehouse A - Section 3',
    lastRestocked: new Date('2024-01-22'),
    expiryDate: new Date('2024-08-15'),
    status: 'in_stock',
  },
];

const dummySuppliers = [
  {
    id: 'SUP-001',
    name: 'Blue Mountain Farms',
    contactPerson: 'Marcus Campbell',
    email: 'marcus@bluemountainfarms.jm',
    phone: '+1-876-555-0123',
    address: 'Blue Mountain Peak, St. Andrew, Jamaica',
    products: ['Blue Mountain Coffee', 'Coffee Beans', 'Roasted Coffee'],
    paymentTerms: 'Net 30',
    leadTime: '7-10 days',
    rating: 4.8,
    totalOrders: 45,
    lastOrderDate: new Date('2024-01-20'),
    status: 'active',
  },
  {
    id: 'SUP-002',
    name: 'Jamaica Spice Co.',
    contactPerson: 'Patrice Williams',
    email: 'patrice@jamaicaspice.com',
    phone: '+1-876-555-0456',
    address: 'Spanish Town, St. Catherine, Jamaica',
    products: ['Jerk Seasoning', 'Allspice', 'Scotch Bonnet Peppers'],
    paymentTerms: 'Net 15',
    leadTime: '5-7 days',
    rating: 4.6,
    totalOrders: 32,
    lastOrderDate: new Date('2024-01-15'),
    status: 'active',
  },
  {
    id: 'SUP-003',
    name: 'Tropical Oils Ltd.',
    contactPerson: 'Devon Brown',
    email: 'devon@tropicaloils.jm',
    phone: '+1-876-555-0789',
    address: 'Montego Bay, St. James, Jamaica',
    products: ['Coconut Oil', 'Palm Oil', 'Castor Oil'],
    paymentTerms: 'Net 45',
    leadTime: '10-14 days',
    rating: 4.2,
    totalOrders: 18,
    lastOrderDate: new Date('2024-01-10'),
    status: 'inactive',
  },
  {
    id: 'SUP-004',
    name: 'Caribbean Snacks Inc.',
    contactPerson: 'Keisha Thompson',
    email: 'keisha@caribbeansnacks.com',
    phone: '+1-876-555-0321',
    address: 'Kingston, Jamaica',
    products: ['Plantain Chips', 'Banana Chips', 'Cassava Chips'],
    paymentTerms: 'Net 30',
    leadTime: '3-5 days',
    rating: 4.7,
    totalOrders: 28,
    lastOrderDate: new Date('2024-01-22'),
    status: 'active',
  },
];

const dummyShippingRates = [
  {
    id: 'SHIP-001',
    carrier: 'FedEx',
    service: 'Ground',
    zone: 'US East Coast',
    baseRate: 8.99,
    perPoundRate: 1.25,
    deliveryTime: '3-5 business days',
    maxWeight: 70,
    status: 'active',
  },
  {
    id: 'SHIP-002',
    carrier: 'FedEx',
    service: 'Express',
    zone: 'US East Coast',
    baseRate: 24.99,
    perPoundRate: 3.50,
    deliveryTime: '1-2 business days',
    maxWeight: 70,
    status: 'active',
  },
  {
    id: 'SHIP-003',
    carrier: 'UPS',
    service: 'Ground',
    zone: 'US West Coast',
    baseRate: 12.99,
    perPoundRate: 1.75,
    deliveryTime: '5-7 business days',
    maxWeight: 150,
    status: 'active',
  },
  {
    id: 'SHIP-004',
    carrier: 'USPS',
    service: 'Priority Mail',
    zone: 'US Nationwide',
    baseRate: 7.99,
    perPoundRate: 0.95,
    deliveryTime: '2-3 business days',
    maxWeight: 70,
    status: 'active',
  },
];

const dummyPurchaseOrders = [
  {
    id: 'PO-001',
    supplier: 'Blue Mountain Farms',
    orderDate: new Date('2024-01-20'),
    expectedDelivery: new Date('2024-01-30'),
    status: 'pending',
    totalAmount: 1250.00,
    items: [
      { product: 'Blue Mountain Coffee - Premium', quantity: 100, unitCost: 12.50 }
    ],
  },
  {
    id: 'PO-002',
    supplier: 'Jamaica Spice Co.',
    orderDate: new Date('2024-01-22'),
    expectedDelivery: new Date('2024-01-29'),
    status: 'shipped',
    totalAmount: 487.50,
    items: [
      { product: 'Jerk Seasoning Blend', quantity: 150, unitCost: 3.25 }
    ],
  },
  {
    id: 'PO-003',
    supplier: 'Tropical Oils Ltd.',
    orderDate: new Date('2024-01-18'),
    expectedDelivery: new Date('2024-02-01'),
    status: 'delivered',
    totalAmount: 875.00,
    items: [
      { product: 'Coconut Oil - Extra Virgin', quantity: 100, unitCost: 8.75 }
    ],
  },
];

export function Inventory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('inventory');

  const getStockStatusConfig = (status: string) => {
    switch (status) {
      case 'in_stock':
        return { color: 'bg-green-100 text-green-800', label: 'In Stock' };
      case 'low_stock':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Low Stock' };
      case 'out_of_stock':
        return { color: 'bg-red-100 text-red-800', label: 'Out of Stock' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const getSupplierStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      case 'suspended':
        return { color: 'bg-red-100 text-red-800', label: 'Suspended' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const getPurchaseOrderStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' };
      case 'shipped':
        return { color: 'bg-blue-100 text-blue-800', label: 'Shipped' };
      case 'delivered':
        return { color: 'bg-green-100 text-green-800', label: 'Delivered' };
      case 'cancelled':
        return { color: 'bg-red-100 text-red-800', label: 'Cancelled' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  // Calculate stats
  const totalItems = dummyInventory.length;
  const lowStockItems = dummyInventory.filter(item => item.status === 'low_stock').length;
  const outOfStockItems = dummyInventory.filter(item => item.status === 'out_of_stock').length;
  const totalInventoryValue = dummyInventory.reduce((sum, item) => sum + (item.currentStock * item.unitCost), 0);
  const activeSuppliers = dummySuppliers.filter(s => s.status === 'active').length;
  const pendingOrders = dummyPurchaseOrders.filter(po => po.status === 'pending').length;

  const renderInventoryTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-jamaica-green" />
            Inventory Items ({dummyInventory.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Stock Level</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Restocked</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyInventory.map((item, index) => {
                  const statusConfig = getStockStatusConfig(item.status);
                  const stockPercentage = (item.currentStock / item.maxStock) * 100;
                  const totalValue = item.currentStock * item.unitCost;
                  
                  return (
                    <TableRow
                      key={item.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-gray-900">{item.productName}</p>
                          <p className="text-sm text-gray-500">{item.category}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {item.sku}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{item.currentStock}</span>
                            <span className="text-sm text-gray-500">/ {item.maxStock}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                stockPercentage > 50 ? 'bg-green-500' : 
                                stockPercentage > 25 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500">Min: {item.minStock}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-semibold text-gray-900">{formatCurrency(totalValue)}</p>
                          <p className="text-sm text-gray-500">
                            {formatCurrency(item.unitCost)} / unit
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-900">{item.supplier}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          <p className="text-sm text-gray-600">{item.location}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(item.lastRestocked)}</p>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Item
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Restock
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Create Purchase Order
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSuppliersTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-jamaica-green" />
            Suppliers ({dummySuppliers.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Supplier</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Terms</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Last Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummySuppliers.map((supplier, index) => {
                  const statusConfig = getSupplierStatusConfig(supplier.status);
                  
                  return (
                    <TableRow
                      key={supplier.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-gray-900">{supplier.name}</p>
                          <p className="text-sm text-gray-500">{supplier.contactPerson}</p>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <p className="text-xs text-gray-500 line-clamp-1">{supplier.address}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" />
                            <p className="text-sm text-gray-600">{supplier.email}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <p className="text-sm text-gray-600">{supplier.phone}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900">{supplier.products.length} products</p>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {supplier.products.join(', ')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900">{supplier.paymentTerms}</p>
                          <p className="text-xs text-gray-500">Lead: {supplier.leadTime}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-gray-900">{supplier.rating}/5</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`w-2 h-2 rounded-full ${
                                    i < Math.floor(supplier.rating) ? 'bg-jamaica-yellow' : 'bg-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">{supplier.totalOrders} orders</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(supplier.lastOrderDate)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Supplier
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Create Order
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <BarChart3 className="w-4 h-4 mr-2" />
                              View Performance
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderShippingTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-jamaica-green" />
            Shipping Rates ({dummyShippingRates.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Carrier & Service</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Base Rate</TableHead>
                  <TableHead>Per Pound</TableHead>
                  <TableHead>Delivery Time</TableHead>
                  <TableHead>Max Weight</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyShippingRates.map((rate, index) => (
                  <TableRow
                    key={rate.id}
                    className="hover:bg-gray-50 transition-colors"
                    style={{
                      opacity: 0,
                      transform: 'translateX(-20px)',
                      animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                    }}
                  >
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium text-gray-900">{rate.carrier}</p>
                        <p className="text-sm text-gray-500">{rate.service}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-gray-400" />
                        <p className="text-sm text-gray-600">{rate.zone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-gray-900">{formatCurrency(rate.baseRate)}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-900">{formatCurrency(rate.perPoundRate)}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <p className="text-sm text-gray-600">{rate.deliveryTime}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">{rate.maxWeight} lbs</p>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800">
                        {rate.status.charAt(0).toUpperCase() + rate.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Rate
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPurchaseOrdersTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-jamaica-green" />
            Purchase Orders ({dummyPurchaseOrders.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Order ID</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead>Expected Delivery</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyPurchaseOrders.map((order, index) => {
                  const statusConfig = getPurchaseOrderStatusConfig(order.status);
                  
                  return (
                    <TableRow
                      key={order.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell>
                        <p className="font-medium text-gray-900">{order.id}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-900">{order.supplier}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(order.orderDate)}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{formatDate(order.expectedDelivery)}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">{order.items.length} items</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Order
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {order.status === 'pending' && (
                              <DropdownMenuItem>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Order
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex justify-end"
        >
          <Button className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Items</p>
                  <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
                </div>
                <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-jamaica-green" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">Inventory tracked</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Low Stock Alerts</p>
                  <p className="text-2xl font-bold text-yellow-600">{lowStockItems}</p>
                </div>
                <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <span className="text-gray-600">Requires restocking</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600">{outOfStockItems}</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <span className="text-gray-600">Immediate action needed</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Inventory Value</p>
                  <p className="text-2xl font-bold text-jamaica-green">{formatCurrency(totalInventoryValue)}</p>
                </div>
                <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-jamaica-green" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+8.2% from last month</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Inventory Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                { id: 'inventory', label: 'Inventory', icon: Package },
                { id: 'suppliers', label: 'Suppliers', icon: Building },
                { id: 'shipping', label: 'Shipping Rates', icon: Truck },
                { id: 'purchase_orders', label: 'Purchase Orders', icon: ShoppingCart },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id
                      ? 'border-jamaica-green text-jamaica-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'inventory' && renderInventoryTab()}
            {activeTab === 'suppliers' && renderSuppliersTab()}
            {activeTab === 'shipping' && renderShippingTab()}
            {activeTab === 'purchase_orders' && renderPurchaseOrdersTab()}
          </div>
        </motion.div>
      </div>
    </>
  );
} 